import { Controller } from '@hotwired/stimulus';

// ⌘K command palette overlay: search + type scope + keyboard navigation. Stays
// in sync with the page filters via the templates:request / templates:sync
// window events.
export default class extends Controller {
  static targets = [
    'backdrop', 'input', 'results', 'scope', 'scopeCount', 'triggerText',
  ];
  static values = { url: String, query: String, category: String };

  connect() {
    this.active = 0;
    this.onGlobalKey = this.onGlobalKey.bind(this);
    this.onSync = this.onSync.bind(this);
    document.addEventListener('keydown', this.onGlobalKey);
    window.addEventListener('templates:sync', this.onSync);
  }

  disconnect() {
    document.removeEventListener('keydown', this.onGlobalKey);
    window.removeEventListener('templates:sync', this.onSync);
  }

  onGlobalKey(event) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.isOpen ? this.close() : this.open();
    } else if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  open(event) {
    if (event) event.preventDefault();
    this.backdropTarget.hidden = false;
    this.inputTarget.value = this.queryValue;
    this.inputTarget.focus();
    this.inputTarget.select();
    this.render();
  }

  close() {
    this.backdropTarget.hidden = true;
  }

  backdropClick() {
    this.close();
  }

  stop(event) {
    event.stopPropagation();
  }

  search() {
    this.queryValue = this.inputTarget.value;
    this.updateTrigger();
    this.dispatchRequest({ query: this.queryValue });
    this.render();
  }

  setScope(event) {
    this.categoryValue = event.currentTarget.dataset.scope;
    this.syncScopes();
    this.dispatchRequest({ category: this.categoryValue });
    this.render();
  }

  keydown(event) {
    const items = this.items;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setActive(this.active + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setActive(this.active - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      items[this.active]?.click();
    }
  }

  async render() {
    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set('q', this.queryValue);
    url.searchParams.set('category', this.categoryValue);
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    const data = await response.json();
    this.resultsTarget.innerHTML = data.results_html;
    this.updateScopeCounts(data.counts);
    this.setActive(0);
  }

  get items() {
    return Array.from(this.resultsTarget.querySelectorAll('.ds-cmdk-item'));
  }

  setActive(index) {
    const items = this.items;
    if (!items.length) return;
    this.active = Math.max(0, Math.min(index, items.length - 1));
    items.forEach((node, i) => node.classList.toggle('is-active', i === this.active));
    items[this.active].scrollIntoView({ block: 'nearest' });
  }

  updateScopeCounts(counts) {
    this.scopeCountTargets.forEach((node) => {
      node.textContent = counts[node.dataset.countFor];
    });
  }

  updateTrigger() {
    if (!this.hasTriggerTextTarget) return;
    const query = this.queryValue.trim();
    const target = this.triggerTextTarget;
    target.textContent = query || target.dataset.placeholder;
    target.classList.toggle('is-filled', query.length > 0);
  }

  onSync(event) {
    const { query, category } = event.detail || {};
    if (query !== undefined) {
      this.queryValue = query;
      this.inputTarget.value = query;
      this.updateTrigger();
    }
    if (category !== undefined) {
      this.categoryValue = category;
      this.syncScopes();
    }
    if (this.isOpen) this.render();
  }

  syncScopes() {
    this.scopeTargets.forEach((node) => {
      node.classList.toggle('is-active', node.dataset.scope === this.categoryValue);
    });
  }

  dispatchRequest(detail) {
    window.dispatchEvent(new CustomEvent('templates:request', { detail }));
  }

  get isOpen() {
    return !this.backdropTarget.hidden;
  }
}
